<template>
  <QSelect
    ref="list"
    v-model="selectedValue"
    borderless
    :bottom-slots="!hideBottomSlots"
    :clearable="isClearable"
    :dense="dense"
    :disable="disable"
    :display-value="displayValue"
    :emit-value="emitValue"
    fill-input
    :filled="filled"
    :hide-selected="hideSelected"
    :input-debounce="inputDebounce"
    :input-style="inputStyle"
    :label="label"
    :map-options="mapOptions"
    :multiple="multiple"
    :new-value-mode="newValueMode"
    :option-disable="optionDisable"
    :option-label="optionLabel"
    :option-value="optionValue"
    :options="filteredOptions"
    :readonly="readonly"
    :rules="rules"
    square
    :stack-label="stackLabel"
    :use-chips="useChips"
    use-input
    @filter="filterOptions"
  >
    <template
      v-if="multiple && canSelectAll"
      #append
    >
      <QItem>
        <QBtn
          flat
          icon="done_all"
          rounded
          size="sm"
          title="Select All"
          @click.stop="selectAll"
        />
      </QItem>
    </template>
    <template #option="scope">
      <slot
        name="option"
        :scope="scope"
      >
        <QItem
          v-if="validOption(scope.opt[optionValue])"
          v-bind="scope.itemProps"
          :style="scope.opt.itemStyle"
        >
          <QItemLabel>
            {{ labelFor(scope.opt) }}
          </QItemLabel>
        </QItem>
      </slot>
    </template>
    <template #no-option>
      <QItem>
        <QItemSection class="text-grey">
          {{ noOptionsMessage || "No results" }}
        </QItemSection>
      </QItem>
    </template>
    <template #hint>
      <div>{{ hint }}</div>
    </template>
  </QSelect>
</template>

<script>
import { sortByStartMatches } from "shared/helpers/array";

export default {
  name: "SelectList",
  props: {
    modelValue: {
      // eslint-disable-next-line
      type: [Number, String, Array, Object, Boolean],
      default: null,
    },
    options: {
      type: Array,
      required: true,
      validator(options) {
        return (
          options.constructor === Array &&
          (!options.length ||
            options.every(
              (value) =>
                Object.prototype.hasOwnProperty.call(value, "name") ||
                Object.prototype.hasOwnProperty.call(value, "label")
            ))
        );
      },
    },
    rules: {
      type: Array,
      default: undefined, // replicate QSelect behaviour, no space below input when prop is omitted
    },
    filterMethod: {
      type: Function,
      default: null,
    },
    label: {
      type: String,
      default: undefined,
    },
    optionValue: {
      type: String,
      default: "id",
    },
    optionLabel: {
      type: [String, Function],
      default: "label",
    },
    displayValue: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: null,
    },
    inputDebounce: {
      type: Number,
      default: 0,
    },
    inputStyle: {
      type: [String, Array, Object],
      default: undefined,
    },
    dense: {
      type: Boolean,
    },
    multiple: {
      type: Boolean,
    },
    emitValue: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true,
    },
    hideBottomSlots: {
      type: Boolean,
    },
    hideSelected: {
      type: Boolean,
    },
    filled: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true,
    },
    clearable: {
      type: Boolean,
    },
    mapOptions: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true,
    },
    disable: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    useChips: {
      type: Boolean,
    },
    stackLabel: {
      type: Boolean,
    },
    canSelectAll: {
      type: Boolean,
    },
    newValueMode: {
      type: String,
      default: undefined,
    },
    noOptionsMessage: {
      type: String,
      default: "",
    },
    optionDisable: {
      type: String,
      default: "disabled",
    },
    noSortOptions: {
      type: Boolean,
    },
    closeOnSelect: {
      type: Boolean,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      filteredOptions: [],
    };
  },
  computed: {
    isLabelFunction() {
      return typeof this.optionLabel === "function";
    },
    isClearable() {
      return (
        this.clearable &&
        Boolean(this.selectedValue) &&
        (this.selectedValue.constructor !== Object ||
          Boolean(this.selectedValue[this.optionValue]))
      );
    },
    selectedValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        let newValue = value;

        if (value === null) {
          newValue = this.multiple ? [] : null;
        }

        this.$emit("update:modelValue", newValue);

        if (this.closeOnSelect) {
          this.$refs.list.hidePopup();
        }
      },
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.filteredOptions = this.options;
      },
    },
  },
  mounted() {
    this.filteredOptions = this.options;
  },
  methods: {
    labelFor(option) {
      if (this.isLabelFunction) {
        return this.optionLabel(option);
      }

      return option[String(this.optionLabel)];
    },
    validOption(value) {
      return value !== null || value !== undefined;
    },
    filterOptions(val, update) {
      if (this.filterMethod) {
        const newResults = this.filterMethod(val);

        if (newResults instanceof Promise) {
          newResults.then((results) => {
            update(() => {
              this.filteredOptions = results;
            });
          });
        } else if (newResults) {
          update(() => {
            this.filteredOptions = newResults;
          });
        }
      } else if (this.noSortOptions) {
        update(() => {
          this.filteredOptions = this.options;
        });
      } else {
        update(() => {
          this.filteredOptions = sortByStartMatches(
            this.options,
            val,
            this.optionLabel,
            1000
          );
        });
      }
    },
    selectAll() {
      const value = this.emitValue
        ? this.options.map((option) => option[this.optionValue])
        : this.options;

      this.$emit("update:modelValue", value);
    },
  },
};
</script>

<style lang="scss" scoped>
.q-select {
  padding: 0;
}

.q-item--active {
  background-color: $input-background !important;
}
</style>
